export class ClipboardService {
  private isMonitoring = false;
  private lastClipboardContent = '';
  private monitorInterval: NodeJS.Timeout | null = null;
  
  public onClipboardChange: ((data: { type: 'text' | 'image'; content: string }) => void) | null = null;

  public async startMonitoring() {
    if (!navigator.clipboard) {
      console.warn('Clipboard API není podporováno');
      return;
    }

    if (this.isMonitoring) {
      console.log('Clipboard monitoring already active');
      return;
    }

    console.log('Starting clipboard monitoring...');
    this.isMonitoring = true;
    this.monitorInterval = setInterval(() => {
      this.checkClipboard();
    }, 1000); // Check every second
  }

  public stopMonitoring() {
    console.log('Stopping clipboard monitoring...');
    this.isMonitoring = false;
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
      this.monitorInterval = null;
    }
  }

  private async checkClipboard() {
    if (!this.isMonitoring || !navigator.clipboard) return;

    try {
      // Try to read text first
      const text = await navigator.clipboard.readText();
      if (text && text !== this.lastClipboardContent && text.trim().length > 0) {
        console.log('Clipboard text detected:', text.substring(0, 50) + '...');
        this.lastClipboardContent = text;
        if (this.onClipboardChange) {
          this.onClipboardChange({ type: 'text', content: text });
        }
        return;
      }
    } catch (err) {
      // Text reading failed, try images
      try {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          const imageType = item.types.find(type => type.startsWith('image/'));
          if (imageType) {
            const blob = await item.getType(imageType);
            const dataUrl = await this.blobToDataUrl(blob);
            if (dataUrl !== this.lastClipboardContent) {
              console.log('Clipboard image detected');
              this.lastClipboardContent = dataUrl;
              if (this.onClipboardChange) {
                this.onClipboardChange({ type: 'image', content: dataUrl });
              }
            }
            break;
          }
        }
      } catch (imageErr) {
        // Silent error - clipboard doesn't contain supported content
        // This is normal and expected
      }
    }
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  public async copyToClipboard(data: { type: 'text' | 'image'; content: string }) {
    try {
      if (data.type === 'text') {
        await navigator.clipboard.writeText(data.content);
      } else if (data.type === 'image') {
        const response = await fetch(data.content);
        const blob = await response.blob();
        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type]: blob })
        ]);
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      throw err;
    }
  }

  public async copyAsPlainText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text:', err);
      throw err;
    }
  }

  public downloadImage(dataUrl: string) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `clipshare-image-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}